/**
 * Oct 20 founder addendum — the Command Center's record of a repair that left "Picked Up — Work Incomplete":
 * the stated reason, the OPTIONAL expected return date (labelled exactly "Expected Return — Not a Scheduled
 * Appointment": nothing is reserved from it), the actual return status, the paused technician work the front office
 * ended at the departure (review still owed / done), the later days released, and the linked continuation. Every
 * departure of the repair is listed, newest first, so earlier return dates stay visible.
 *
 * Read-only (CORE-AE :42 "MUST NOT become a second place where a truth is decided"): one reader,
 * repair_departure_history (shop members only, RLS-equivalent check server-side). Nothing when there was no
 * incomplete pickup (CORE-AE :46 honest empty = the block is simply absent); a failed read says so.
 */
import { useEffect, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { readDepartureHistory, type DepartureHistory, type DepartureRecord } from '@/lib/foh/pickup/departureActs';

export const EXPECTED_RETURN_LABEL = 'Expected Return — Not a Scheduled Appointment';

function at(iso: string | null, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? formatInTimeZone(d, tz, 'EEE MMM d, h:mm a') : iso;
}
/** A date-only value shown as written (never shifted by a time zone). */
export function dateOnlyWords(date: string | null): string {
  if (!date) return 'Not given';
  const d = new Date(`${date}T12:00:00Z`);
  return Number.isFinite(d.getTime()) ? formatInTimeZone(d, 'UTC', 'EEE MMM d, yyyy') : date;
}

export function returnStatusWords(d: DepartureRecord, tz: string): string {
  if (!d.returnedAt) return 'Not returned yet — the customer has the vehicle';
  const back = `Returned ${at(d.returnedAt, tz)}${d.returnedByName ? `, received by ${d.returnedByName}` : ''}`;
  if (d.continuationTicketId) {
    return `${back} · continued on ${d.continuationTicketNumber != null ? `RO #${d.continuationTicketNumber}` : 'a linked continuation'}`;
  }
  return `${back} · awaiting scheduling`;
}

export function TicketDepartureRecord({ ticketId, timeZone }: { ticketId: string; timeZone: string }) {
  const [state, setState] = useState<{ status: 'loading' | 'ok' | 'failed'; data?: DepartureHistory | null }>({ status: 'loading' });

  useEffect(() => {
    let alive = true;
    setState({ status: 'loading' });
    readDepartureHistory(ticketId)
      .then((data) => { if (alive) setState({ status: data ? 'ok' : 'failed', data }); })
      .catch(() => { if (alive) setState({ status: 'failed' }); });
    return () => { alive = false; };
  }, [ticketId]);

  if (state.status === 'loading') return null;
  if (state.status === 'failed') {
    return <p className="text-xs text-muted-foreground" data-testid="ticket-departure-unavailable">Pickup history could not be read just now.</p>;
  }
  const deps = state.data?.departures ?? [];
  if (deps.length === 0) return null;

  return (
    <section className="space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="ticket-departure-record">
      <h3 className="text-sm font-semibold">Picked up — work incomplete</h3>
      {deps.map((d, i) => (
        <div key={d.incompletePickupId} className="space-y-1 text-sm" data-testid={`ticket-departure-${d.incompletePickupId}`}>
          {i > 0 && <hr className="my-2" />}
          <div>
            <span className="text-muted-foreground">Left </span>{at(d.pickedUpAt, timeZone)}
            {d.departureTicketNumber != null && <span className="text-muted-foreground"> · from RO #{d.departureTicketNumber}</span>}
            {d.recordedByName && <span className="text-muted-foreground"> · recorded by {d.recordedByName}</span>}
          </div>
          <div data-testid="ticket-departure-reason"><span className="text-muted-foreground">Reason: </span>{d.reason}</div>
          <div data-testid="ticket-departure-expected-return">
            <span className="font-medium">{EXPECTED_RETURN_LABEL}: </span>{dateOnlyWords(d.expectedReturnDate)}
          </div>
          <div data-testid="ticket-departure-return-status"><span className="text-muted-foreground">Return: </span>{returnStatusWords(d, timeZone)}</div>
          {d.releasedPausedWork.length > 0 && (
            <ul className="text-xs text-muted-foreground" data-testid="ticket-departure-released-work">
              {d.releasedPausedWork.map((w, k) => (
                <li key={k}>
                  Paused work by {w.technician_name ?? 'a technician'} (paused {at(w.paused_at, timeZone)}, {w.pause_reason},
                  {' '}{w.measured_minutes} min measured) ended at the departure by the front office —
                  {w.review_pending ? ' time review still owed by the technician' : ' time reviewed'}
                  {Array.isArray(w.diagnostic_outcomes_owed) && w.diagnostic_outcomes_owed.length > 0 ? ' · diagnostic outcome still owed' : ''}
                </li>
              ))}
            </ul>
          )}
          {d.withdrawnDays.length > 0 && (
            <div className="text-xs text-muted-foreground" data-testid="ticket-departure-withdrawn-days">
              Later days released (not re-booked): {d.withdrawnDays.map((w) => w.scheduled_date ?? 'unscheduled').join(', ')}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
